import { useCallback, useEffect, useState } from 'react'
import { BadgeCheck, ImageOff, RefreshCw, ShieldX } from 'lucide-react'
import { ApiError } from '@/lib/api-client'
import { formatDateTimeCompact } from '@/lib/datetime'
import { formatMinorCurrency } from '@/lib/format'
import { queryKeys } from '@/lib/query-client'
import type { AdminTripEdit } from '@/types/api'
import { useAdminMutation } from '@/hooks/useAdminMutation'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Field } from '@/components/ui/Field'
import { Textarea } from '@/components/ui/Input'
import { KeyValue, KeyValueGrid } from '@/components/ui/RowActions'
import { Spinner } from '@/components/ui/Spinner'
import { fetchTripEditProof, verifyTripEditPayment } from './api'
import styles from './TripEditsPage.module.css'

interface VerifyTripEditPaymentDialogProps {
  edit: AdminTripEdit | null
  onClose: () => void
}

type ProofState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; url: string }
  | { status: 'error'; message: string }

/**
 * Confirming that an Instapay transfer for a change actually arrived.
 *
 * This is the moment the change becomes real: nothing before it has touched the
 * booking, so a receipt that turns out to be for the wrong amount costs a
 * refusal rather than an unpicking. Refusing puts the change back to awaiting
 * payment with the note attached — the customer sends the right receipt instead
 * of starting the whole request again.
 *
 * The receipt is shown inside the dialog, because the decision is made against
 * the evidence and an operator should not have to remember what they just saw.
 */
export function VerifyTripEditPaymentDialog({ edit, onClose }: VerifyTripEditPaymentDialogProps) {
  const open = edit !== null
  const [note, setNote] = useState('')
  const [proof, setProof] = useState<ProofState>({ status: 'idle' })

  useEffect(() => {
    if (open) setNote('')
  }, [open, edit?.id])

  // A blob URL pins its data until revoked. Without this an operator working
  // through a queue would accumulate every receipt they had opened.
  useEffect(() => {
    return () => {
      if (proof.status === 'ready') URL.revokeObjectURL(proof.url)
    }
  }, [proof])

  // A different change is a different image — dropped rather than left on
  // screen, so one customer's receipt never appears under another's name.
  useEffect(() => {
    setProof((current) => {
      if (current.status === 'ready') URL.revokeObjectURL(current.url)
      return { status: 'idle' }
    })
  }, [edit?.id])

  const loadProof = useCallback(async () => {
    if (!edit) return
    setProof({ status: 'loading' })
    try {
      const { objectUrl } = await fetchTripEditProof(edit.id)
      setProof({ status: 'ready', url: objectUrl })
    } catch (error) {
      setProof({
        status: 'error',
        message:
          error instanceof ApiError || error instanceof Error
            ? error.message
            : 'The receipt could not be loaded.',
      })
    }
  }, [edit])

  const mutation = useAdminMutation({
    mutationFn: (accepted: boolean) =>
      verifyTripEditPayment(edit!.id, accepted, note.trim() || undefined),
    successTitle: 'Decision recorded',
    successDescription: (updated) =>
      updated.status === 'APPLIED'
        ? 'The booking has been updated and the customer notified.'
        : 'The customer has been asked to send the receipt again. Their booking is unchanged.',
    errorTitle: 'The decision was not recorded',
    invalidate: [
      queryKeys.tripEdits.all,
      queryKeys.tripRequests.all,
      queryKeys.trips.all,
      queryKeys.dashboard.all,
    ],
    onSuccess: () => onClose(),
  })

  // A refusal with no reason is not reviewable, and the customer has to know
  // what to fix. Accepting needs no essay — "the money is there".
  const canRefuse = note.trim().length >= 3 && !mutation.isPending

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Did this transfer arrive?"
      size="md"
      busy={mutation.isPending}
      description="Confirming applies the change to the booking and tells the customer. Refusing leaves the booking as it is and asks them to send the receipt again."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="danger"
            iconLeft={<ShieldX aria-hidden="true" />}
            disabled={!canRefuse}
            onClick={() => mutation.mutate(false)}
          >
            Not received
          </Button>
          <Button
            variant="primary"
            iconLeft={<BadgeCheck aria-hidden="true" />}
            loading={mutation.isPending}
            disabled={mutation.isPending}
            onClick={() => mutation.mutate(true)}
          >
            Confirm and apply
          </Button>
        </>
      }
    >
      {edit ? (
        <div className={styles.dialogBody}>
          <section className={styles.summary}>
            <KeyValueGrid>
              <KeyValue label="Customer">{edit.customerName ?? `#${edit.userId}`}</KeyValue>
              <KeyValue label="Booking">
                <span className={styles.mono}>{edit.bookingReference ?? `#${edit.tripRequestId}`}</span>
              </KeyValue>
              <KeyValue label="Amount due">
                <strong className={styles.amount}>
                  {formatMinorCurrency(edit.extraAmountMinor, edit.currency)}
                </strong>
              </KeyValue>
              <KeyValue label="Receipt sent">{formatDateTimeCompact(edit.proofUploadedAt)}</KeyValue>
            </KeyValueGrid>
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Transfer receipt</h3>
            {/* Loaded on demand: fetching every receipt as a queue opens pulls
                images nobody looked at, and each stays in memory until revoked. */}
            {proof.status === 'idle' ? (
              <Button variant="secondary" onClick={() => void loadProof()}>
                Show the receipt
              </Button>
            ) : null}
            {proof.status === 'loading' ? <Spinner /> : null}
            {proof.status === 'ready' ? (
              <img src={proof.url} alt="Proof of the customer's transfer" className={styles.proofImage} />
            ) : null}
            {proof.status === 'error' ? (
              <div className={styles.warning} role="alert">
                <ImageOff aria-hidden="true" />
                <p>{proof.message}</p>
                <Button
                  size="sm"
                  variant="ghost"
                  iconLeft={<RefreshCw aria-hidden="true" />}
                  onClick={() => void loadProof()}
                >
                  Try again
                </Button>
              </div>
            ) : null}
          </section>

          <Field
            label="Note for the customer"
            hint="Required when the transfer has not arrived — they need to know what to correct."
          >
            {({ id, describedBy, invalid }) => (
              <Textarea
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                rows={2}
                value={note}
                maxLength={500}
                disabled={mutation.isPending}
                placeholder="The transfer was for 100, not 150…"
                onChange={(event) => setNote(event.target.value)}
              />
            )}
          </Field>
        </div>
      ) : null}
    </Dialog>
  )
}
